/**
 * ZVault - Zod Validation Schemas for the Vault API
 *
 * Endpoints: /api/vault/*
 * Controller: vault/vault.controller.ts
 *
 * The vault stores organization secrets (BYOK API keys + pool-provisioned keys).
 * Every response is METADATA ONLY — decrypted secret values are never returned
 * over the wire, so none of these schemas carry a secret-value field.
 *
 * `provider` is intentionally `z.string()` (not an enum): the set of provider
 * ids differs between the frontend union and the backend catalog, and the
 * backend casts the value at its boundary. A strict enum would reject otherwise
 * valid responses. `category` and `source` are closed enums (VaultCategory /
 * VaultSource value objects) — not Prisma DbEnums.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** Open provider identifier — see file header for why this is not an enum. */
const VaultProviderIdSchema = z.string();

/** Provider grouping — mirrors the VaultCategory value object. */
export const VAULT_CATEGORIES = [
  "ai_model",
  "browser_automation",
  "oauth",
  "webhook",
  "custom"
] as const;
const VaultCategorySchema = z.enum(VAULT_CATEGORIES);

/** How a secret was provisioned — mirrors the VaultSource value object. */
export const VAULT_SOURCES = ["pool", "byok"] as const;
const VaultSourceSchema = z.enum(VAULT_SOURCES);

/** Secret metadata — no decrypted values. */
const SecretMetadataSchema = z.object({
  id: z.string(),
  provider: VaultProviderIdSchema,
  category: VaultCategorySchema,
  source: VaultSourceSchema,
  label: z.string().nullable(),
  hasPoolSlot: z.boolean(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string() // ISO string
});

export type VaultSecretMetadata = z.infer<typeof SecretMetadataSchema>;

const ListSecretsResponseSchema = z.object({
  secrets: z.array(SecretMetadataSchema),
  count: z.number()
});

const PoolStatusResponseSchema = z.object({
  provider: VaultProviderIdSchema,
  total: z.number(),
  available: z.number(),
  assigned: z.number()
});

const ProviderDefinitionSchema = z.object({
  id: VaultProviderIdSchema,
  category: VaultCategorySchema,
  displayName: z.string(),
  requiredSecrets: z.array(z.string()),
  optionalSecrets: z.array(z.string()),
  description: z.string().optional(),
  docsUrl: z.string().optional(),
  poolAvailable: z.boolean()
});

const ListProvidersResponseSchema = z.object({
  providers: z.array(ProviderDefinitionSchema)
});

const AvailableModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: VaultProviderIdSchema
});

const ConfiguredProviderSchema = z.object({
  provider: VaultProviderIdSchema,
  displayName: z.string(),
  source: VaultSourceSchema,
  models: z.array(AvailableModelSchema)
});

const ConfiguredProvidersResponseSchema = z.object({
  providers: z.array(ConfiguredProviderSchema)
});

const AvailableProviderSchema = z.object({
  provider: VaultProviderIdSchema,
  displayName: z.string(),
  description: z.string().optional(),
  docsUrl: z.string().optional(),
  poolAvailable: z.boolean(),
  poolSlotsRemaining: z.number().nullable(), // null when pool not available
  alreadyConfigured: z.boolean()
});

const AvailableProvidersResponseSchema = z.object({
  providers: z.array(AvailableProviderSchema)
});

const ClaimPoolResultSchema = z.object({
  message: z.string()
});

/** DELETE endpoints return a bare acknowledgement (no 204 — the envelope needs a body). */
const VaultOkSchema = z.object({
  success: z.literal(true)
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

const ProviderPathVarsSchema = z.object({
  provider: z.string()
});

const StoreSecretBodySchema = z.object({
  provider: z.string().min(1),
  secrets: z.record(z.string(), z.string()),
  label: z.string().optional()
});

const UpdateSecretBodySchema = z.object({
  secrets: z.record(z.string(), z.string()).optional(),
  label: z.string().nullable().optional()
});

const ClaimPoolSlotBodySchema = z.object({
  provider: z.string().min(1),
  label: z.string().optional()
});

// ═══════════════════════════════════════════════════════════════
// ZVAULT MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZVault = {
  /** GET /api/vault/secrets — list all secrets for the org (metadata only). */
  ListSecrets: {
    method: "GET" as const,
    path: "/api/vault/secrets",
    Response: ApiSuccessSchema(ListSecretsResponseSchema)
  },

  /** GET /api/vault/secrets/:provider — secret metadata for one provider. */
  GetSecret: {
    method: "GET" as const,
    path: "/api/vault/secrets/:provider",
    PathVars: ProviderPathVarsSchema,
    Response: ApiSuccessSchema(SecretMetadataSchema)
  },

  /** POST /api/vault/secrets — store a new BYOK secret. */
  StoreSecret: {
    method: "POST" as const,
    path: "/api/vault/secrets",
    Body: StoreSecretBodySchema,
    Response: ApiSuccessSchema(SecretMetadataSchema)
  },

  /** PATCH /api/vault/secrets/:provider — update an existing BYOK secret. */
  UpdateSecret: {
    method: "PATCH" as const,
    path: "/api/vault/secrets/:provider",
    PathVars: ProviderPathVarsSchema,
    Body: UpdateSecretBodySchema,
    Response: ApiSuccessSchema(SecretMetadataSchema)
  },

  /** DELETE /api/vault/secrets/:provider — delete a BYOK secret. */
  DeleteSecret: {
    method: "DELETE" as const,
    path: "/api/vault/secrets/:provider",
    PathVars: ProviderPathVarsSchema,
    Response: ApiSuccessSchema(VaultOkSchema)
  },

  /** POST /api/vault/pool/claim — claim a pool key for a provider. */
  ClaimPoolSlot: {
    method: "POST" as const,
    path: "/api/vault/pool/claim",
    Body: ClaimPoolSlotBodySchema,
    Response: ApiSuccessSchema(ClaimPoolResultSchema)
  },

  /** DELETE /api/vault/pool/:provider — release a pool key back to the pool. */
  ReleasePoolSlot: {
    method: "DELETE" as const,
    path: "/api/vault/pool/:provider",
    PathVars: ProviderPathVarsSchema,
    Response: ApiSuccessSchema(VaultOkSchema)
  },

  /** GET /api/vault/pool/status/:provider — shared cross-org pool capacity. */
  GetPoolStatus: {
    method: "GET" as const,
    path: "/api/vault/pool/status/:provider",
    PathVars: ProviderPathVarsSchema,
    Response: ApiSuccessSchema(PoolStatusResponseSchema)
  },

  /** GET /api/vault/providers — static catalog of supported providers. */
  ListProviders: {
    method: "GET" as const,
    path: "/api/vault/providers",
    Response: ApiSuccessSchema(ListProvidersResponseSchema)
  },

  /** GET /api/vault/providers/configured — providers the org has credentials for, with models. */
  GetConfiguredProviders: {
    method: "GET" as const,
    path: "/api/vault/providers/configured",
    Response: ApiSuccessSchema(ConfiguredProvidersResponseSchema)
  },

  /** GET /api/vault/providers/available — configurable providers with pool availability. */
  GetAvailableProviders: {
    method: "GET" as const,
    path: "/api/vault/providers/available",
    Response: ApiSuccessSchema(AvailableProvidersResponseSchema)
  }
} as const;
